<script>
    import {fade} from 'svelte/transition';

    import classnames from 'clsx';

    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import {activateOnKey} from '@js/modules/a11y';

    import Computation from '@js/modules/computation';
    import MapRequest from '@js/modules/maps/mapRequest';
    import {highlightParts} from '@js/modules/searchResults';

    import {fetchAutocomplete} from '@js/actions/autocompleteActions';

    import Icon from '@js/components/themes/Icon.svelte';
    import Bounce from '@js/components/themes/Bounce.svelte';

    import closeIcon from '@js/components/icons/closeIcon';
    import searchIcon from '@js/components/icons/searchIcon';
    import tagIcon from '@js/components/icons/tagIcon';
    import {PLACE_ICON, RIDE_TYPE_ICON} from '@js/components/themes/icons';

    import {AutocompleteLimit, MaxSearchRate} from '@js/components/modules/constants';
    import {trackOpenMenu} from '@js/components/themes/actions/clickOutside';

    import('@css/components/autocompletion.scss');

    let {
        id,
        class: className,
        placeholder,
        searchButton = I18n.t('js.helpers.buttons.search'),
        searchFeatures, // all (by default), location, ride, tag, user
        searchFilters = {},
        initialValue,
        searchValue = $bindable({value: ''}),
        minQueryLength = 2,
        rideResultsFirst = false,
        autoFocus = false,
        useFirstQueryOnEnter = true,
        // (query) => results the page finds itself (the plan page's planned rides): listed first, in their own section
        localSearch = null,
        onSubmit
    } = $props();

    const LIMIT_SOURCE_RESULT = 3;

    const SECTION_ORDER = ['planned', 'location', 'ride', 'tag'];
    const SECTION_ORDER_RIDES_FIRST = ['planned', 'ride', 'location', 'tag'];
    const SECTION_TITLE = {
        planned: 'js.autocomplete.sections.planned',
        location: 'js.autocomplete.sections.places',
        ride: 'js.autocomplete.sections.rides',
        tag: 'js.autocomplete.sections.tags'
    };


    let searchInput = $state();
    let isFetching = $state(false);
    let autocompleteResults = $state([]);
    let highlightedIndex = $state(null);

    if (initialValue) {
        searchValue.value = initialValue;
    }

    let _proximity = {
        lat: parseFloat(window.userLatitude) || 0,
        lng: parseFloat(window.userLongitude) || 0,
        // Proximity bias radius around the user's real position: 10 keeps nearby places first while
        // staying wide enough to surface mid/long-range targets (~300-800 km) over exact namesakes
        // abroad. Higher (12+) is too tight and lets a far exact match outrank the intended place.
        zoom: 10
    };
    // Outside the supported countries there is no centre: (0, 0) searches without a bias.
    if (!_proximity.lat && window.countryMapCenter) {
        _proximity = {
            lat: window.countryMapCenter[0],
            lng: window.countryMapCenter[1],
            // Broad bias: this is the country centroid, not the user's actual location.
            zoom: 7
        };
    }

    let _autocompleteRequest;
    const _autocompleteRequestAbort = {};
    let _locationRequest;
    const _locationRequestAbort = {};

    let enterPressed = false;

    const _onAutocompleteResult = (results) => {
        let newResults = [];

        if (results.tags?.length) {
            newResults = newResults.concat(
                results.tags
                    .map((tag) => ({
                        uniqueId: `tag-${tag.id}`,
                        label: I18n.t('js.autocomplete.tag'),
                        icon: tagIcon,
                        title: tag.name,
                        type: 'tag',
                        id: tag.id,
                        key: tag.name,
                        value: tag.name
                    }))
                    .limit(LIMIT_SOURCE_RESULT)
            );
        }

        if (results.rides?.length) {
            newResults = newResults.concat(
                results.rides
                    .map((ride) => ({
                        uniqueId: `ride-${ride.id}`,
                        label: I18n.t(`js.autocomplete.ride.${ride.rideType}`),
                        type: 'ride',
                        id: ride.id,
                        key: ride.name,
                        value: ride.name,
                        rideType: ride.rideType,
                        link: ride.link,
                        icon: RIDE_TYPE_ICON[ride.rideType],
                        title: ride.name,
                        // Namesake routes stay distinct: their activity and length tell them apart
                        subtitle: [
                            I18n.t(`js.autocomplete.ride.${ride.rideType}`),
                            ride.distance ? Computation.formatDistance(ride.distance, {smartUnit: true, kUnit: false, decimalLimit: 1}) : null,
                            ride.formattedDuration
                        ].filter(Boolean).join(' · ')
                    }))
                    .limit(LIMIT_SOURCE_RESULT)
            );
        }

        // if (results.users?.length) {
        //     newResults.userResults = results.users;
        // }

        return newResults;
    };

    const _onLocationResult = (features) => {
        let newResults = [];

        if (features?.length) {
            newResults = newResults.concat(
                features.map((location) => ({
                    uniqueId: `location-${location.fullName.replace(/ /g, '_')}`,
                    label: ['peak', 'poi', 'other'].includes(location.placeType) ? I18n.t(`js.autocomplete.location.${location.placeType}`) : undefined,
                    type: 'location',
                    key: location.fullName,
                    query: location.name,
                    value: location.fullName,
                    placeType: location.placeType,
                    icon: PLACE_ICON[location.placeType] || PLACE_ICON.other,
                    title: location.name,
                    subtitle: location.placeName,
                    center: location.center,
                    bbox: location.bbox
                }))
            );
        }

        return newResults;
    };

    const _autocomplete = Utils.debounce((query, proximity) => {
        if (query.length < minQueryLength) {
            return;
        }

        const requests = [];
        const localResults = localSearch ? localSearch(query) : [];
        // A pick made while this search runs: its local results must not open the list again
        const pickRound = _pickRound;

        if (!searchFeatures || searchFeatures.includes('location')) {
            if (_locationRequestAbort?.signal) {
                _locationRequestAbort.signal.abort();
            }

            _locationRequest = MapRequest.locationSearch(query, {
                locale: I18n.locale,
                proximity: proximity,
                limit: AutocompleteLimit,
                autocomplete: true,
                sortByPlaceType: true,
                sortByMatchingName: true,
                sortByLocaleCountry: true
            }, {
                abort: _locationRequestAbort
            });

            requests.push(_locationRequest);
        }

        if (!searchFeatures || searchFeatures.includes('ride') || searchFeatures.includes('tag') || searchFeatures.includes('user')) {
            if (_autocompleteRequestAbort?.signal) {
                _autocompleteRequestAbort.signal.abort();
            }

            const selectedTypes = [];
            if (searchFeatures) {
                if (searchFeatures.includes('ride')) {
                    selectedTypes.push('ride');
                }
                if (searchFeatures.includes('tag')) {
                    selectedTypes.push('tag');
                }
                if (searchFeatures.includes('user')) {
                    selectedTypes.push('user');
                }
            }

            _autocompleteRequest = fetchAutocomplete({
                selectedTypes,
                query: searchValue.value,
                limit: AutocompleteLimit,
                ...searchFilters
            }, {
                abort: _autocompleteRequestAbort
            });

            requests.push(_autocompleteRequest);
        }

        Promise.allSettled(requests)
            .then((results) => {
                isFetching = false;

                let newResults = pickRound === _pickRound ? [...localResults] : [];

                results.forEach((result, promiseIndex) => {
                    if (result.status === 'fulfilled' && result.value) {
                        if (promiseIndex === 0) {
                            newResults = newResults.concat(_onLocationResult(result.value));
                        } else {
                            newResults = newResults.concat(_onAutocompleteResult(result.value));
                        }
                        // } else if (result.status === 'rejected') {
                        //     pushError(result.reason);
                    }
                });

                // Grouped by section (stable sort): keyboard order follows the displayed order
                const sectionOrder = rideResultsFirst ? SECTION_ORDER_RIDES_FIRST : SECTION_ORDER;
                autocompleteResults = newResults
                    .map((result, index) => ({result, index}))
                    .sort((a, b) => (sectionOrder.indexOf(a.result.type) - sectionOrder.indexOf(b.result.type)) || (a.index - b.index))
                    .map(({result}) => result);

                if (enterPressed) {
                    enterPressed = false;

                    setInputVal(autocompleteResults[highlightedIndex]);
                    closeAutocompletion();
                }
            });
    }, MaxSearchRate);

    const filterResults = () => {
        isFetching = true;
        // Typing again drops the picked entry (arrow-highlighted or chosen): Enter submits what is typed
        highlightedIndex = null;
        searchValue = {value: searchValue.value};

        if (searchValue) {
            _autocomplete(searchValue.value, _proximity);
        } else {
            autocompleteResults = [];
            highlightedIndex = null;
        }
    };

    const clickOutside = (element, callbackFunction) => {
        function onClick(event) {
            if (!element.contains(event.target)) {
                callbackFunction();
            }
        }

        document.body.addEventListener('click', onClick);

        return {
            update(newCallbackFunction) {
                callbackFunction = newCallbackFunction;
            },
            destroy() {
                document.body.removeEventListener('click', onClick);
            }
        };
    };

    const clearInput = (event) => {
        event.preventDefault();

        isFetching = false;
        searchValue = {value: ''};
        searchInput.focus();

        onSubmit({...searchValue});
    };

    const closeAutocompletion = () => {
        autocompleteResults = [];
        highlightedIndex = null;
        const element = document.getElementById(id);
        if (element) {
            element.focus();
        }
    };

    $effect(() => (autocompleteResults.length > 0 ? trackOpenMenu(closeAutocompletion) : undefined));

    let _pickRound = 0;

    const setInputVal = (selectedResult) => {
        _pickRound += 1;
        isFetching = false;
        enterPressed = false;

        searchValue = selectedResult || searchValue;
        closeAutocompletion();

        onSubmit({...searchValue});
    };

    const submit = () => {
        if (useFirstQueryOnEnter) {
            if (highlightedIndex === null) {
                highlightedIndex = 0;
            }

            // Wait fetching is finish to use the first entry as result
            if (!isFetching) {
                setInputVal(autocompleteResults[highlightedIndex]);

                closeAutocompletion();

                _autocompleteRequestAbort.signal?.abort();
                _locationRequestAbort.signal?.abort();
            }
        } else {
            _autocompleteRequestAbort.signal?.abort();
            _locationRequestAbort.signal?.abort();

            // An entry picked with the arrow keys is the one submitted
            setInputVal(highlightedIndex !== null ? autocompleteResults[highlightedIndex] : undefined);
        }
    };

    const navigateList = (event) => {
        if (event.key === 'ArrowDown' && highlightedIndex <= autocompleteResults.length - 1) {
            highlightedIndex === null ? highlightedIndex = 0 : highlightedIndex += 1;
        } else if (event.key === 'ArrowUp' && highlightedIndex !== null) {
            highlightedIndex === 0 ? highlightedIndex = autocompleteResults.length - 1 : highlightedIndex -= 1;
        } else if (event.key === 'Enter') {
            event.preventDefault();

            enterPressed = true;

            submit();
        } else if (event.key === 'Escape') {
            closeAutocompletion();
        } else if (!searchValue.value && isFetching) {
            isFetching = false;
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        enterPressed = true;

        submit();
    };

    $effect(() => {
        return () => {
            _autocompleteRequestAbort.signal?.abort();
            _locationRequestAbort.signal?.abort();
        };
    });
</script>

<div class={classnames('relative text-left', className)}>
    <label for={id}
           class="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
        {searchButton}
    </label>

    <div class="hidden md:flex absolute inset-y-0 start-0 items-center ps-3 pointer-events-none">
        <Icon class="w-6 h-6 text-gray-500 dark:text-gray-400"
              icon={searchIcon}/>
    </div>

    <div class="autocompletion-input">
        <!-- svelte-ignore a11y_autofocus -->
        <input type="search"
               id={id}
               class="block w-full p-4 ps-4 md:ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500"
               placeholder={placeholder}
               required
               autocomplete="off"
               autocorrect="off"
               autocapitalize="off"
               spellcheck="false"
               autofocus={autoFocus}
               onkeydown={navigateList}
               oninput={filterResults}
               bind:this={searchInput}
               bind:value={searchValue.value}>
    </div>

    <div class="absolute end-2.5 bottom-2.5 flex items-center justify-center">
        {#if isFetching}
            <Bounce color="primary"/>
        {/if}

        {#if searchValue.value && !isFetching}
            <!-- svelte-ignore a11y_missing_attribute -->
            <a class="ml-2 md:ml-4 cursor-pointer"
               role="button"
               tabindex="0"
               aria-label={I18n.t('js.a11y.clear_search')}
               onclick={clearInput}
               onkeydown={activateOnKey(clearInput)}>
                <Icon class="w-6 h-6"
                      icon={closeIcon}/>
            </a>
        {/if}

        <button type="submit"
                class="ml-2 md:ml-4 text-white bg-primary-700 hover:bg-primary-800 focus:outline-hidden focus:ring-2 focus:ring-primary-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                onclick={handleSubmit}>
            <span class="block md:hidden">
                <Icon class="w-5 h-5"
                      icon={searchIcon}/>
            </span>
            <span class="hidden md:block">
                {searchButton}
            </span>
        </button>
    </div>

    {#if autocompleteResults.length > 0}
        <ul class="absolute inset-x-0 mt-1 py-1 max-h-[70vh] overflow-y-auto bg-white border border-gray-200 z-dropdown rounded-lg shadow-lg"
            role="listbox"
            use:clickOutside={closeAutocompletion}
            transition:fade={{ duration: 150 }}>
            {#each autocompleteResults as autocompleteResult, i (autocompleteResult.uniqueId)}
                {#if i === 0 || autocompleteResults[i - 1].type !== autocompleteResult.type}
                    <li class="list-none px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
                        class:border-t={i > 0}
                        class:border-gray-100={i > 0}
                        class:mt-1={i > 0}
                        role="presentation">
                        {I18n.t(SECTION_TITLE[autocompleteResult.type] || SECTION_TITLE.location)}
                    </li>
                {/if}

                <!-- The keyboard-picked row is the one Enter submits: outlined, not just tinted (3:1 contrast) -->
                <li class="group list-none flex items-center gap-3 mx-1 px-2 py-1.5 text-sm rounded-md cursor-pointer hover:bg-gray-100 active:bg-primary-100"
                    class:bg-primary-100={i === highlightedIndex}
                    class:ring-2={i === highlightedIndex}
                    class:ring-inset={i === highlightedIndex}
                    class:ring-primary-700={i === highlightedIndex}
                    role="option"
                    aria-selected={i === highlightedIndex}
                    tabindex="0"
                    onclick={() => setInputVal(autocompleteResult)}
                    onkeydown={activateOnKey(() => setInputVal(autocompleteResult))}>
                    <span class="inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-full bg-primary-100 text-primary-800">
                        <Icon class="w-4 h-4"
                              icon={autocompleteResult.icon || PLACE_ICON.other}/>
                    </span>

                    <span class="flex flex-col min-w-0">
                        <span class="truncate text-gray-900">
                            {#each highlightParts(autocompleteResult.title || autocompleteResult.value, searchValue.value) as part, partIndex (partIndex)}
                                {#if part.match}<strong class="font-semibold">{part.text}</strong>{:else}{part.text}{/if}
                            {/each}
                        </span>

                        {#if autocompleteResult.subtitle}
                            <span class="truncate text-xs text-gray-500">
                                {autocompleteResult.subtitle}
                            </span>
                        {/if}
                    </span>
                </li>
            {/each}
        </ul>
    {/if}
</div>
